import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import { Route } from 'react-router-dom';

import { redirectToIdPortalLogout } from 'utils/redirectToIdPortalLogout';

/**
 * A wrapper around React Router's Route component that handles authentication logic.
 *
 * This component ensures that protected routes are only accessible to authenticated users.
 * It redirects users based on their authentication status:
 *
 * - If user is not authenticated, redirects to the login page with the current location saved in state
 * - If user is authenticated but password is expired, redirects to the password expired page
 * - If user is authenticated and password is not expired, renders the route normally
 *
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - The components to render when authentication passes
 * @param {boolean} props.isAuthenticated - Whether the user is currently authenticated
 * @param {Object} props.routeProps - Additional props to pass to the underlying Route component
 * @returns {React.ReactElement} A Route component with authentication logic
 */
const PrivateRoute = ({ children, isAuthenticated, ...routeProps }) => {
  useEffect(() => {
    // If user is not authenticated, redirect to ID portal logout so portal clears token and shows login
    if (!isAuthenticated) {
      redirectToIdPortalLogout();
    }
  }, [isAuthenticated]);

  if (!isAuthenticated) return null;

  return <Route {...routeProps} />;
};

PrivateRoute.propTypes = {
  children: PropTypes.node,
  isAuthenticated: PropTypes.bool,
};

export default PrivateRoute;
